export const theme = {
    colors: {
        accent: '#1238bf', //digi
        accentLighter: '#143fe5', //digi
        main: '#007cb0'
    }
};

import {
    createTheme,
    PaletteOptions,
    Theme,
    ThemeOptions,
} from "@mui/material/styles";
import {
    error,
    grey,
    primary,
    secondary,
    success,
    warning,
    main,
    custom,
    info,
} from "./themeColors";
import { BreakpointsOptions, PaletteMode } from "@mui/material";

declare module "@mui/material/styles" {
    interface Theme {
        custom: any;
    }
    // allow configuration using `createTheme`
    interface ThemeOptions {
        custom?: any;
    }
}

const fontSize = 14;

const fontFamily = [
    "Arimo",
    "Noto Sans",
    "Open Sans",
    "Roboto",
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Oxygen",
    "Ubuntu",
    "Cantarell",
    "Fira Sans",
    "Droid Sans",
    "Helvetica Neue",
    "sans-serif",
].join(",");

// export const customTheme = createTheme({
//   breakpoints: {
//     values: {
//       xs: 0,
//       sm: 600,
//       md: 960,
//       lg: 1440,
//       xl: 1920,
//     },
//   },
// });

export const lightMode: PaletteOptions = {
    mode: "light",
    primary: {
        light: primary["300"],
        main: primary["400"],
        dark: primary["500"],
        ...primary,
    },
    secondary: {
        ...secondary,
        light: secondary["300"],
        main: secondary["400"],
        dark: secondary["500"],
    },
    error: {
        ...error,
        light: error["300"],
        main: error["400"],
        dark: error["500"],
    },
    warning: {
        ...warning,
        light: warning["300"],
        main: warning["400"],
        dark: warning["500"],
    },
    success,
    info,
    text: {
        primary: grey.light[900],
        secondary: grey.light[800],
        disabled: grey.light[700],
    },
    divider: grey.light[400],
    grey: { ...grey.light },
    background: {
        default: main.light.body_bg,
        paper: main.light.content_bg,
    },
};

export const darkMode: PaletteOptions = {
    mode: "dark",
    primary: {
        light: primary["300"],
        main: primary["400"],
        dark: primary["500"],
        ...primary,
    },
    secondary: {
        ...secondary,
        light: secondary["300"],
        main: secondary["400"],
        dark: secondary["500"],
    },
    error: {
        ...error,
        light: error["300"],
        main: error["400"],
        dark: error["500"],
    },
    warning: {
        ...warning,
        light: warning["300"],
        main: warning["400"],
        dark: warning["500"],
    },
    success,
    info,
    text: {
        primary: grey.dark[500],
        secondary: grey.dark[600],
        disabled: grey.dark[900],
    },
    divider: grey.dark[400],
    grey: { ...grey.dark },
    background: {
        default: main.dark.body_bg,
        paper: main.dark.body_bg,
    },
};

const breakpoints: BreakpointsOptions = {
    values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1440,
        xl: 1920,
    },
};

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
    custom,
    palette: mode === "light" ? lightMode : darkMode,
    typography: {
        fontSize,
        fontFamily,
        htmlFontSize: 16,
        body1: { fontSize },
        body2: { fontSize },
        small: {
            fontSize: "12px",
            fontWeight: "regular",
            color: grey.light[900],
        },
    },
    breakpoints: breakpoints,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                ul: {
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                },

                select: {
                    ":disabled": {
                        backgroundColor: grey[200],
                    },
                },

                input: {
                    ":disabled": {
                        backgroundColor: grey[200],
                    },
                },

                p: {
                    lineHeight: 1.75,
                },
                a: {
                    textDecoration: "none",
                    color: primary[400],
                },
                button: {
                    fontFamily,
                    fontSize,
                },

                ".MuiRating-sizeSmall": {
                    fontSize: "20px",
                },
                "#nprogress .bar": {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    height: "3px !important",
                    borderRadius: "0px 300px 300px 0px !important",
                    zIndex: 1031,
                    background: `${primary[500]} !important`,
                    overflow: "hidden",
                },
                "#nprogress .peg": {
                    boxShadow: `0 0 10px ${primary[500]}, 0 0 5px ${primary[500]} !important`,
                },
            },
        },
        MuiPagination: {
            defaultProps: {
                variant: "outlined",
                color: "primary",
            },
        },
        MuiSelect: {
            styleOverrides: {},
        },

        MuiTextField: {
            defaultProps: {
                size: "small",
                variant: "outlined",
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    paddingTop: 8,
                    paddingBottom: 8,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    textTransform: "unset",
                    minWidth: 0,
                    minHeight: 0,
                },
            },
            defaultProps: {
                color: "inherit",
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: "8px",
                },
            },
        },
        // MuiContainer: {
        //   styleOverrides: {
        //     root: {
        //       [customTheme.breakpoints.up("sm")]: {
        //         paddingLeft: "1rem",
        //         paddingRight: "1rem",
        //       },
        //     },
        //   },
        // },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 8,
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                body1: {
                    fontSize: "14px",
                    lineHeight: "150%",
                    fontWeight: "normal",
                    color: grey.light[900],
                },

                body2: {
                    fontSize: "12px",
                    color: grey.light[900],
                },
                h1: {
                    fontSize: "30px",
                    fontWeight: "bold",
                    lineHeight: "110%",
                    color: grey.light[900],
                },
                h2: {
                    fontSize: "25px",
                    fontWeight: "bold",
                    lineHeight: "120%",
                    color: grey.light[900],
                },
                h3: {
                    fontSize: "20px",
                    fontWeight: "bold",
                    lineHeight: "130%",
                    color: grey.light[900],
                },
                h4: {
                    fontSize: "17px",
                    fontWeight: "bold",
                    lineHeight: "130%",
                    color: grey.light[900],
                },
                h5: {
                    fontSize: "16px",
                    fontWeight: "bold",
                    lineHeight: "140%",
                    color: grey.light[900],
                },
                h6: {
                    fontSize: "14px",
                    fontWeight: "bold",
                    lineHeight: "140%",
                    color: grey.light[900],
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: "2px",
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    // paddingTop: "3px",
                    // paddingBottom: "3px",
                    color: grey.light[500],
                    "&.Mui-checked": {
                        color: grey.light[900],
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: grey.light[800],
                    "&.Mui-focused": {
                        // color: `${grey[800]}!important`,
                        color: grey.light[800],
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: grey.light[400],
                },
            },
        },
    },
});

declare module "@mui/material/styles" {
    interface TypographyVariants {
        small: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        small?: React.CSSProperties;
    }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        small: true;
    }
}

export type MuiThemeProps = Theme;

export default getDesignTokens;
